/**
 * Copy text to the clipboard with a graceful fallback for older
 * browsers and non-secure contexts (file://, http://, etc.) where
 * `navigator.clipboard.writeText` is unavailable.
 *
 * The fallback uses a hidden `<textarea>` + `document.execCommand("copy")`,
 * which is deprecated but still universally supported. We deliberately
 * avoid promising "the modern API or nothing" because the admin UI may
 * be opened in places (preview environments, embedded webviews) where
 * the secure-context guarantees aren't met.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
    if (
        typeof navigator !== "undefined" &&
        navigator.clipboard !== undefined &&
        typeof navigator.clipboard.writeText === "function"
    ) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch {
            // Fall through to the textarea path. Some browsers reject
            // clipboard writes when the page isn't focused, even in
            // secure contexts.
        }
    }

    if (typeof document === "undefined") return false;

    const textarea = document.createElement("textarea");
    textarea.value = text;
    // Off-screen but still focusable; visibility:hidden / display:none
    // blocks selection in some browsers.
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    textarea.style.top = "0";
    document.body.appendChild(textarea);
    textarea.select();

    let success = false;
    try {
        success = document.execCommand("copy");
    } catch {
        success = false;
    } finally {
        document.body.removeChild(textarea);
    }
    return success;
}
